/* Add a joiner: IT's part of onboarding, as a guided flow.
 *
 * A new starter needs several decisions in order (who they are, what role, what access, what kit),
 * and the person drawer is a poor place for that: it is a sidebar for EDITING somebody who is
 * already set up. So this is a full page with a step bar, and the drawer stays for later changes.
 *
 * NOTHING IS WRITTEN UNTIL THE LAST STEP. The choices are a draft, kept in session storage so a
 * refresh does not lose them. "Hand to HR" then carries them out one step at a time (lib/joiner.ts)
 * and says which step failed, with a Retry that does not repeat what already worked. The contract
 * with the Onboarding app, and why it is shaped this way, is docs/joiner-onboarding.md.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { APPS, type AppRole } from '@hird/access';
import { useDepots } from '../../hooks/useDepots';
import { groupOf } from '../../lib/deviceGroups';
import {
  accessChanges,
  allDone,
  applyTemplate,
  blankDraft,
  isEmailAddress,
  joinerProblems,
  levelOf,
  levelRole,
  planHandToHr,
  runHandToHr,
  type JoinerOps,
  type JoinerStepId,
  type StepResult,
} from '../../lib/joiner';
import { isOffboarding } from '../../lib/lifecycle';
import { isRealPerson, isUnclassified } from '../../lib/registers';
import type { IJoinerDraft, IPerson, IRoleTemplate } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import { PRODUCT } from '../../lib/product';
import { Button, EmptyState, LoadError, useToast } from '../ui';
import s from './joiner.module.scss';

type Step = 'role' | 'access' | 'equipment' | 'review';

const STEPS: { id: Step | 'account'; label: string }[] = [
  { id: 'account', label: 'Account' },
  { id: 'role', label: 'Role and details' },
  { id: 'access', label: 'Access' },
  { id: 'equipment', label: 'Equipment' },
  { id: 'review', label: 'Review' },
];

const ROLE_LABEL: Record<string, string> = {
  none: 'No access',
  user: 'User',
  admin: 'Admin',
  superadmin: 'Super Admin',
};

const storeKey = (id: string): string => `blueprint.joiner.${id}`;

function loadDraft(id: string, fallback: IJoinerDraft): IJoinerDraft {
  try {
    const raw = sessionStorage.getItem(storeKey(id));
    return raw ? { ...fallback, ...(JSON.parse(raw) as Partial<IJoinerDraft>) } : fallback;
  } catch {
    return fallback;
  }
}

/** The tiers an app actually has, so nobody is offered one that does nothing. */
function tiersFor(appId: string): AppRole[] {
  const app = APPS.find((a) => a.appId === appId);
  const out: AppRole[] = ['none', 'user'];
  if (app?.overrides?.admin) out.push('admin');
  if (app?.overrides?.superadmin) out.push('superadmin');
  return out;
}

export function JoinerWizard(): JSX.Element {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const svc = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const depots = useDepots();

  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const templates = useQuery({
    queryKey: qk.joinerTemplates,
    queryFn: () => svc.joiners!.templates(),
    enabled: !!svc.joiners,
  });
  const cases = useQuery({
    queryKey: qk.joinerCases,
    queryFn: () => svc.joiners!.cases(),
    enabled: !!svc.joiners,
  });

  const person = useMemo(() => (people.data ?? []).find((p) => p.id === id), [people.data, id]);
  const step = (params.get('step') as Step | null) ?? 'role';
  const go = (next: Step): void => setParams({ step: next }, { replace: true });

  const [draft, setDraft] = useState<IJoinerDraft | null>(null);
  useEffect(() => {
    if (person && !draft) setDraft(loadDraft(person.id, blankDraft(person)));
  }, [person, draft]);
  useEffect(() => {
    if (person && draft) {
      try { sessionStorage.setItem(storeKey(person.id), JSON.stringify(draft)); } catch { /* a refresh then loses the draft; nothing else */ }
    }
  }, [person, draft]);

  const change = (patch: Partial<IJoinerDraft>): void => setDraft((d) => (d ? { ...d, ...patch } : d));

  /* ── Step 1: who ─────────────────────────────────────────────────────────── */
  if (!id) {
    const taken = new Set((cases.data ?? []).map((c) => c.upn.toLowerCase()));
    const waiting = (people.data ?? [])
      .filter((p) => !p.onboarded && !isOffboarding(p) && (isUnclassified(p) || isRealPerson(p)))
      .filter((p) => !taken.has((p.upn || '').toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));

    return (
      <Frame step="account" name="">
        <h1 className={s.title}>Who is joining?</h1>
        <p className={s.lede}>
          Pick the account IT has already created in Entra. It appears here the morning after, when the
          sync has brought it in.
        </p>
        {!svc.joiners && <NotConfigured />}
        {people.isError ? (
          <LoadError what="people" error={people.error} />
        ) : people.isLoading || cases.isLoading ? (
          <p className={s.lede}>Loading…</p>
        ) : waiting.length === 0 ? (
          <EmptyState icon="ti-user-plus" title="Nobody is waiting">
            New accounts appear here the morning after they are created in Entra.
          </EmptyState>
        ) : (
          <div className={s.list}>
            {waiting.map((p) => (
              <Link key={p.id} to={`/joiner/${p.id}?step=role`} className={s.person}>
                <span className={s.avatar} aria-hidden>{p.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('')}</span>
                <span className={s.personText}>
                  <b>{p.name}</b>
                  <small>{p.email || p.upn}</small>
                </span>
                <i className="ti ti-chevron-right" aria-hidden />
              </Link>
            ))}
          </div>
        )}
      </Frame>
    );
  }

  if (people.isLoading || !person || !draft) {
    return (
      <Frame step="role" name="">
        {people.isLoading ? <p className={s.lede}>Loading…</p> : <EmptyState icon="ti-user-question" title="That account was not found">
          <Link to="/joiner">Choose somebody</Link>
        </EmptyState>}
      </Frame>
    );
  }

  const template: IRoleTemplate | null = (templates.data ?? []).find((t) => t.id === draft.templateId) ?? null;

  return (
    <Frame step={step} name={person.name}>
      {step === 'role' && (
        <RoleStep
          person={person}
          draft={draft}
          templates={templates.data ?? []}
          loading={templates.isLoading}
          depotNames={depots.map((d) => d.name)}
          departments={[...new Set((people.data ?? []).map((p) => p.dept).filter(Boolean))].sort()}
          onTemplate={(t) => setDraft((d) => (d ? applyTemplate(d, t) : d))}
          onChange={change}
          onNext={() => go('access')}
        />
      )}
      {step === 'access' && (
        <AccessStep
          draft={draft}
          template={template}
          onChange={change}
          onBack={() => go('role')}
          onNext={() => go('equipment')}
        />
      )}
      {step === 'equipment' && (
        <EquipmentStep
          draft={draft}
          template={template}
          devices={(devices.data ?? []).filter((d) => !d.user && groupOf(d.type) === 'endpoint')}
          onChange={change}
          onBack={() => go('access')}
          onNext={() => go('review')}
        />
      )}
      {step === 'review' && (
        <ReviewStep
          person={person}
          draft={draft}
          template={template}
          deviceList={devices.data ?? []}
          onBack={() => go('equipment')}
          onEdit={go}
          onHandedOver={() => {
            try { sessionStorage.removeItem(storeKey(person.id)); } catch { /* nothing to clean */ }
            void qc.invalidateQueries({ queryKey: qk.people });
            void qc.invalidateQueries({ queryKey: qk.devices });
            void qc.invalidateQueries({ queryKey: qk.joinerCases });
            void qc.invalidateQueries({ queryKey: qk.audit });
            toast(`${person.name} is with HR.`, 'ok');
            navigate('/onboarding');
          }}
        />
      )}
    </Frame>
  );
}

/* ── The frame: header and step bar ─────────────────────────────────────────── */

function Frame({ step, name, children }: { step: Step | 'account'; name: string; children: React.ReactNode }): JSX.Element {
  const at = STEPS.findIndex((x) => x.id === step);
  return (
    <div className={s.page}>
      <header className={s.header}>
        <span className={s.mark} aria-hidden>B</span>
        <b>{PRODUCT.name}</b>
        <span className={s.sep} aria-hidden>/</span>
        <span className={s.muted}>Add a joiner</span>
        {name && (<><span className={s.sep} aria-hidden>/</span><span>{name}</span></>)}
        <span className={s.grow} />
        <Link to="/onboarding" className={s.cancel}>Cancel</Link>
      </header>
      <ol className={s.steps} aria-label="Steps">
        {STEPS.map((x, i) => (
          <li key={x.id} className={`${s.stepPill} ${i < at ? s.stepDone : ''} ${i === at ? s.stepNow : ''}`} aria-current={i === at ? 'step' : undefined}>
            {i < at ? <i className="ti ti-check" aria-hidden /> : null}
            {i + 1} {x.label}
          </li>
        ))}
      </ol>
      <main className={s.body}>{children}</main>
    </div>
  );
}

function NotConfigured(): JSX.Element {
  return (
    <div className={s.notice} role="status">
      <i className="ti ti-plug-off" aria-hidden />
      <span>
        The Onboarding app is not set up for this build yet (<code>VITE_ONBOARDING_SITE_ID</code>), so a
        joiner cannot be handed to HR. You can look around, but the last step will not work.
      </span>
    </div>
  );
}

/* ── Step 2: role and details ───────────────────────────────────────────────── */

function RoleStep(props: {
  person: IPerson;
  draft: IJoinerDraft;
  templates: IRoleTemplate[];
  loading: boolean;
  depotNames: string[];
  departments: string[];
  onTemplate: (t: IRoleTemplate | null) => void;
  onChange: (p: Partial<IJoinerDraft>) => void;
  onNext: () => void;
}): JSX.Element {
  const { person, draft, templates, depotNames, departments } = props;
  const welcome = (draft.welcomeTo ?? '').trim();
  const welcomeBad = !!welcome && !isEmailAddress(welcome);
  const complete = !!draft.title.trim() && !!draft.loc.trim() && !!draft.start.trim() && !!welcome && !welcomeBad;
  return (
    <>
      <h1 className={s.title}>Role and details</h1>
      <p className={s.lede}>
        Pick the role. The template fills in access, training and kit, and you can change any of it on
        the next steps.
      </p>

      <div className={s.card}>
        <span className={s.avatar} aria-hidden>{person.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('')}</span>
        <span className={s.personText}><b>{person.name}</b><small>{person.email || person.upn}</small></span>
        <span className={s.ok}>From Entra</span>
      </div>

      <fieldset className={s.group}>
        <legend>Role template</legend>
        <div className={s.templates}>
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`${s.template} ${draft.templateId === t.id ? s.templateOn : ''}`}
              aria-pressed={draft.templateId === t.id}
              onClick={() => props.onTemplate(t)}
            >
              <b>{t.title}</b>
              <small>{t.depot || 'Any depot'}</small>
            </button>
          ))}
          <button
            type="button"
            className={`${s.template} ${s.templateNone} ${!draft.templateId ? s.templateOn : ''}`}
            aria-pressed={!draft.templateId}
            onClick={() => props.onTemplate(null)}
          >
            <b>No template</b>
            <small>Set everything by hand</small>
          </button>
        </div>
        {props.loading && <small className={s.muted}>Loading templates…</small>}
        {!props.loading && templates.length === 0 && (
          <small className={s.muted}>No templates have been made yet. Everything is set by hand for now.</small>
        )}
      </fieldset>

      <div className={s.fields}>
        <label>Job title
          <input value={draft.title} onChange={(e) => props.onChange({ title: e.target.value })} />
        </label>
        <label>Depot
          <select value={draft.loc} onChange={(e) => props.onChange({ loc: e.target.value })}>
            <option value="">Choose…</option>
            {depotNames.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label>Department
          <input list="joiner-depts" value={draft.dept} onChange={(e) => props.onChange({ dept: e.target.value })} />
          <datalist id="joiner-depts">{departments.map((d) => <option key={d} value={d} />)}</datalist>
        </label>
        <label>Start date
          <input type="date" value={draft.start} onChange={(e) => props.onChange({ start: e.target.value })} />
        </label>
        <label>Send their welcome email to
          <input type="email" inputMode="email" autoComplete="off" value={draft.welcomeTo ?? ''} placeholder="A personal address is fine"
            onChange={(e) => props.onChange({ welcomeTo: e.target.value })} />
          <small className={welcomeBad ? s.warn : s.muted}>
            {welcomeBad ? 'That is not an email address. Give one, like name@example.com.' : 'The address they actually read, a personal one is fine. The welcome email is sent to it when you hand over, so they can sign in on their first day.'}
          </small>
        </label>
      </div>
      <p className={s.muted}>Manager is read from Entra and shows once the account has one.</p>

      <Footer onNext={props.onNext} nextLabel="Next: Access" disabled={!complete} why={complete ? '' : 'Job title, depot and start date are needed.'} />
    </>
  );
}

/* ── Step 3: access ─────────────────────────────────────────────────────────── */

function AccessStep(props: {
  draft: IJoinerDraft;
  template: IRoleTemplate | null;
  onChange: (p: Partial<IJoinerDraft>) => void;
  onBack: () => void;
  onNext: () => void;
}): JSX.Element {
  const { draft, template } = props;
  const lvl = levelOf(draft);
  const wanted = (appId: string): AppRole => draft.apps[appId] ?? levelRole(appId, lvl);
  const set = (appId: string, role: AppRole): void => props.onChange({ apps: { ...draft.apps, [appId]: role } });
  const source = (appId: string): string => {
    const t = template?.apps[appId];
    const d = draft.apps[appId];
    if (d === undefined) return levelRole(appId, lvl) === 'none' ? 'Not granted' : `Hub Level ${lvl}`;
    if (t !== undefined && t === d) return 'Template';
    return 'Added by you';
  };
  const changes = accessChanges(draft.apps, lvl);

  return (
    <>
      <h1 className={s.title}>Access</h1>
      <p className={s.lede}>
        {template ? `Filled in from the ${template.title} template. ` : ''}
        Change anything that is not right. Access is only granted when you hand over at the end.
      </p>

      <div className={s.card}>
        <span className={s.personText}>
          <b>Hub access</b>
          <small>
            {lvl === 2
              ? 'Level 2 makes them an administrator of the Hub: they get the top tier of nearly every app and can manage other people. Only for IT and the people who run it.'
              : 'Level 1 is what lets them sign in to the Hub. It is right for nearly everybody.'}
          </small>
        </span>
        <span className={s.levelPick} role="radiogroup" aria-label="Hub level">
          <button type="button" role="radio" aria-checked={lvl === 1} className={`${s.chip} ${lvl === 1 ? s.chipOn : ''}`} onClick={() => props.onChange({ level: 1 })}>Level 1 · Staff</button>
          <button type="button" role="radio" aria-checked={lvl === 2} className={`${s.chip} ${lvl === 2 ? s.chipOn : ''}`} onClick={() => props.onChange({ level: 2 })}>Level 2 · Administrator</button>
        </span>
      </div>

      <div className={s.table} role="table" aria-label="App access">
        <div className={s.row} role="row">
          <span className={s.th} role="columnheader">App</span>
          <span className={s.th} role="columnheader">Tier</span>
          <span className={s.th} role="columnheader">Set by</span>
        </div>
        {APPS.map((a) => (
          <div key={a.appId} className={`${s.row} ${source(a.appId) === 'Added by you' ? s.rowChanged : ''}`} role="row">
            <span role="cell"><b>{a.appName}</b></span>
            <span role="cell">
              <select
                aria-label={`Tier for ${a.appName}`}
                value={wanted(a.appId)}
                onChange={(e) => set(a.appId, e.target.value as AppRole)}
              >
                {tiersFor(a.appId).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
              </select>
            </span>
            <span role="cell" className={s.muted}>{source(a.appId)}</span>
          </div>
        ))}
      </div>
      <p className={s.muted}>
        {changes.length === 0
          ? 'Nothing beyond Level 1 will be written.'
          : `${changes.length} app group${changes.length === 1 ? '' : 's'} will be written, on top of Level 1.`}
      </p>

      <Footer onBack={props.onBack} onNext={props.onNext} nextLabel="Next: Equipment" />
    </>
  );
}

/* ── Step 4: equipment ──────────────────────────────────────────────────────── */

function EquipmentStep(props: {
  draft: IJoinerDraft;
  template: IRoleTemplate | null;
  devices: { id: string; tag: string; type: string; make: string; model: string }[];
  onChange: (p: Partial<IJoinerDraft>) => void;
  onBack: () => void;
  onNext: () => void;
}): JSX.Element {
  const { draft, devices, template } = props;
  const toggle = (devId: string): void =>
    props.onChange({
      deviceIds: draft.deviceIds.includes(devId) ? draft.deviceIds.filter((x) => x !== devId) : [...draft.deviceIds, devId],
    });
  return (
    <>
      <h1 className={s.title}>Equipment</h1>
      <p className={s.lede}>
        {template && template.kit.length > 0 ? `This role usually receives: ${template.kit.join(', ')}. ` : ''}
        Pick any unassigned kit that is ready. Skip this if it has not arrived yet.
      </p>
      {devices.length === 0 ? (
        <EmptyState icon="ti-device-laptop" title="No unassigned devices">
          Assign kit from the Devices screen when it arrives.
        </EmptyState>
      ) : (
        <div className={s.list}>
          {devices.map((d) => (
            <label key={d.id} className={`${s.pick} ${draft.deviceIds.includes(d.id) ? s.pickOn : ''}`}>
              <input type="checkbox" checked={draft.deviceIds.includes(d.id)} onChange={() => toggle(d.id)} />
              <span className={s.personText}><b>{d.tag}</b><small>{d.type} · {d.make} {d.model}</small></span>
            </label>
          ))}
        </div>
      )}
      <Footer onBack={props.onBack} onNext={props.onNext} nextLabel="Next: Review" />
    </>
  );
}

/* ── Step 5: review and hand over ───────────────────────────────────────────── */

function ReviewStep(props: {
  person: IPerson;
  draft: IJoinerDraft;
  template: IRoleTemplate | null;
  deviceList: { id: string; tag: string; user: string | null }[];
  onBack: () => void;
  onEdit: (s: Step) => void;
  onHandedOver: () => void;
}): JSX.Element {
  const { person, draft, template } = props;
  const svc = useServices();
  const plan = planHandToHr(draft, props.deviceList);
  const problems = joinerProblems(draft, person);
  const changes = accessChanges(draft.apps, levelOf(draft));

  const [results, setResults] = useState<Record<string, StepResult | { state: 'running'; message: string }>>({});
  const [running, setRunning] = useState(false);

  const ops: JoinerOps = {
    saveDetails: async (patch) => { await svc.people.update(person.id, patch); },
    setLevel: async (level) => { await svc.directory.setLevel(person.graphId as string, level); },
    setAppRole: async (appId, role) => { await svc.directory.setAppRole(person.graphId as string, appId, role); },
    assign: async (deviceId) => {
      const r = await svc.devices.assign(deviceId, person.id, 'Onboarding');
      return { problems: r.problems };
    },
    /* The folder AND the welcome email, now. IT gives them access up front, so the welcome goes with it:
       they can sign in before their first day. It goes to the address given in the wizard, which is often
       a personal one. If the email does not send, this step fails and a retry sends it (the folder work is
       safe to repeat), rather than the case opening for somebody who was never told. */
    createFolder: async () => {
      const to = (draft.welcomeTo ?? '').trim();
      const o = await svc.onboarding.onboard({
        upn: person.upn, mail: person.email, welcomeTo: to, displayName: person.name, depot: draft.loc, sendWelcome: true,
      });
      if (!o.ok || !o.emailSent) throw new Error(o.message || `The welcome email to ${to} did not send.`);
      // Stamped only now that it really went, so the register and the "To welcome" list agree.
      await svc.people.update(person.id, { welcomeSentOn: new Date().toISOString() });
      return { message: `Folder made, and the welcome email sent to ${to}.` };
    },
    openCase: async () => {
      if (!svc.joiners) throw new Error('The Onboarding app is not set up for this build, so there is nowhere to open the case.');
      const opened = await svc.joiners.openCase({
        name: person.name, upn: person.upn, graphId: person.graphId, templateId: draft.templateId || undefined,
        jobTitle: draft.title, depot: draft.loc, department: draft.dept, start: draft.start,
        welcomeTo: (draft.welcomeTo ?? '').trim() || undefined,
      });
      /* Then HR are told somebody is ready, once: a case that was already open (a retry) says nothing again.
         A failed email is said in the step and never fails the hand-over, so the case is not opened twice. */
      if (!opened.created) return 'Ready for HR. Their case was already open, so HR were not emailed again.';
      const to = await svc.joiners.hrRecipients();
      if (to.length === 0) return 'Ready for HR. Nobody is set to be emailed yet: add the HR team in Onboarding, under Settings.';
      const told = await svc.onboarding.notify({ name: person.name, jobTitle: draft.title, depot: draft.loc, start: draft.start, to });
      return told.ok
        ? `Ready for HR, and ${told.sent === 1 ? '1 person has' : `${told.sent} people have`} been emailed.`
        : `Ready for HR, but the email to HR did not go: ${told.message}.`;
    },
  };

  const run = async (): Promise<void> => {
    setRunning(true);
    const done = new Set<JoinerStepId>(
      Object.entries(results).filter(([, r]) => r.state === 'done' || r.state === 'skipped').map(([k]) => k as JoinerStepId),
    );
    const out = await runHandToHr({
      draft,
      personId: person.id,
      devices: props.deviceList,
      done,
      ops,
      onStep: (r) => setResults((prev) => ({ ...prev, [r.id]: r })),
    });
    setRunning(false);
    if (allDone(out)) props.onHandedOver();
  };

  const started = Object.keys(results).length > 0;
  const failed = Object.values(results).some((r) => r.state === 'failed');

  return (
    <>
      <h1 className={s.title}>Review and hand to HR</h1>
      <p className={s.lede}>Check it over. Nothing has been changed yet: access and the folder are created when you press the button.</p>

      <div className={s.summary}>
        <div className={s.card2}><div><b>Account</b><button type="button" className={s.link} onClick={() => props.onEdit('role')}>Edit</button></div><span>{person.name}</span><small>{person.email || person.upn}</small></div>
        <div className={s.card2}><div><b>Role and details</b><button type="button" className={s.link} onClick={() => props.onEdit('role')}>Edit</button></div><span>{draft.title} · {draft.loc || 'no depot'}</span><small>{draft.dept || 'No department'} · starts {draft.start || 'no date'}</small><small>Welcome email to {(draft.welcomeTo ?? '').trim() || 'to be confirmed by HR'}</small></div>
        <div className={s.card2}><div><b>Access</b><button type="button" className={s.link} onClick={() => props.onEdit('access')}>Edit</button></div><span>Hub Level {levelOf(draft)}{levelOf(draft) === 2 ? ' (administrator)' : ''}{changes.length ? ` and ${changes.length} app${changes.length === 1 ? '' : 's'}` : ''}</span><small>{template ? `${template.title} template` : 'No template'}{changes.length ? ': ' + changes.map((c) => c.appName).join(', ') : ''}</small></div>
        <div className={s.card2}><div><b>Equipment</b><button type="button" className={s.link} onClick={() => props.onEdit('equipment')}>Edit</button></div><span>{draft.deviceIds.length === 0 ? 'Nothing assigned' : `${draft.deviceIds.length} device${draft.deviceIds.length === 1 ? '' : 's'}`}</span><small>{draft.deviceIds.map((id) => props.deviceList.find((d) => d.id === id)?.tag).filter(Boolean).join(', ') || 'Skip for now'}</small></div>
      </div>

      <section className={s.panel} aria-label="What happens">
        <h2>When you press Hand to HR</h2>
        <ol className={s.plan}>
          {plan.map((p) => {
            const r = results[p.id];
            return (
              <li key={p.id} className={r ? s[`plan_${r.state}`] : ''}>
                <span className={s.planIcon} aria-hidden>
                  {r?.state === 'done' || r?.state === 'skipped' ? <i className="ti ti-check" /> : r?.state === 'failed' ? <i className="ti ti-alert-triangle" /> : r?.state === 'running' ? <i className="ti ti-loader-2" /> : null}
                </span>
                <span>
                  <b>{p.label}</b>
                  <small>{r && r.state === 'failed' ? r.message : p.detail}</small>
                </span>
              </li>
            );
          })}
        </ol>
      </section>

      {problems.length > 0 && (
        <div className={s.notice} role="alert">
          <i className="ti ti-alert-triangle" aria-hidden />
          <span>{problems.join(' ')}</span>
        </div>
      )}
      {!svc.joiners && <NotConfigured />}

      <Footer
        onBack={props.onBack}
        onNext={() => void run()}
        nextLabel={failed ? 'Retry' : 'Hand to HR'}
        disabled={running || problems.length > 0}
        busy={running}
        why={started && !failed && !running ? '' : ''}
      />
    </>
  );
}

function Footer(props: {
  onBack?: () => void;
  onNext: () => void;
  nextLabel: string;
  disabled?: boolean;
  busy?: boolean;
  why?: string;
}): JSX.Element {
  return (
    <div className={s.footer}>
      {props.onBack ? <button type="button" className={s.back} onClick={props.onBack}>Back</button> : <span />}
      <span className={s.grow} />
      {props.why && <small className={s.muted}>{props.why}</small>}
      {!props.why && <small className={s.muted}>Nothing is changed until the last step.</small>}
      <Button primary disabled={props.disabled} spinning={props.busy} icon={props.busy ? 'ti-loader-2' : undefined} onClick={props.onNext}>{props.nextLabel}</Button>
    </div>
  );
}
